import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import Icon from '../../Icons'
import { LEGACY_BASE_PATH } from './legacy-path'
import { SERVICE_COLORS, SERVICE_ICONS, SERVICE_CATEGORIES } from '../../../helpers/constants'
import ConfirmPopup from '../../Popups/ConfirmPopup'
import { getAssetLink } from '../../../helpers/get-asset-link'
import TextAreaField from '../../FormLegacy/FormFieldsV2/TextAreaField'
import TextField from '../../FormLegacy/FormFieldsV2/TextField'
import ToggleField from '../../FormLegacy/FormFieldsV2/ToggleField'
import ImageField from '../../FormLegacy/FormFieldsV2/ImageField'
import SelectField from '../../FormLegacy/FormFieldsV2/SelectField'
import RadioField from '../../FormLegacy/FormFieldsV2/RadioField'
import {
  createShopCollectionItem,
  deleteShopCollectionItem,
  selectShopServiceById,
  updateShopCollectionItem,
} from '../../../reducers/Organisation/organisationShopReducer'
import { selectBookingTypesForDropdown } from '../../../reducers/Organisation/bookTypesReducer'

const OrganisationServicesModal = ({ params }) => {
  const { t } = useTranslation(['organisation', 'common'])
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const service = useSelector((state) => selectShopServiceById(state)(params.serviceId))
  const bookingTypes = useSelector(selectBookingTypesForDropdown)

  const [formPending, setFormPending] = useState(false)
  const [formError, setFormError] = useState('')
  const [modalConfirmOpened, setModalConfirmOpened] = useState(false)

  const closeModal = () => {
    navigate(LEGACY_BASE_PATH)
    setFormPending(false)
    setModalConfirmOpened(false)
  }

  const openConfirmModal = (e) => {
    e.preventDefault()
    setModalConfirmOpened(true)
  }

  const closeConfirmModal = () => {
    setModalConfirmOpened(false)
  }

  const cancelServiceChanges = (e) => {
    e.preventDefault()
    closeModal()
  }

  const saveService = ({
    id,
    name,
    description,
    imageInCategory,
    image,
    icon,
    color,
    category,
    active,
    activeType,
    bookingType,
    internalName,
  }) => {
    const productData = {
      name,
      description,
      icon,
      color,
      category,
      personal:
        category !== 'Group Training' || category !== 'Nutrition Services' || category !== 'Gift',
      active,
      activeType,
      bookingType,
      internalName,
    }

    if (imageInCategory.file) {
      productData.imageInCategory = imageInCategory.file
    }
    if (image.file) {
      productData.image = image.file
    }

    if (isDuplicate()) {
      productData.duplicateId = params.serviceId
    }

    setFormPending(true)
    setFormError('')

    const saveAction =
      isNew() || isDuplicate()
        ? dispatch(createShopCollectionItem(productData))
        : dispatch(updateShopCollectionItem(productData, id))

    saveAction
      .then(() => {
        closeModal()
        setFormPending(false)
      })
      .catch((err) => {
        setFormPending(false)
        setFormError(err.message || t('organisation:shared.serverError'))
        throw new Error(err)
      })
  }

  const deleteService = () => {
    setFormError('')
    dispatch(deleteShopCollectionItem(service.id))
      .then(() => {
        closeConfirmModal()
        closeModal()
      })
      .catch(() => {
        setFormError(t('organisation:shared.serverError'))
        closeConfirmModal()
      })
  }

  const isNew = () => params.serviceId === 'new'
  const isDuplicate = () => params.mode === 'duplicate'

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t('common:validation.required')),
    internalName: Yup.string().required(t('common:validation.required')),
    description: Yup.string().required(t('common:validation.required')),
    category: Yup.string().required(t('common:validation.required')),
  })

  return (
    <div
      className={`modal modal--organisation modal--service ${
        params.serviceId && !params.skuId ? 'is-active' : ''
      }`}
    >
      <div className="modal-background" onClick={cancelServiceChanges} />
      <div className="modal-content">
        <div className="box">
          <header className="modal__header">
            <span>
              {isNew()
                ? t('organisation:services.modal.titleNew')
                : isDuplicate()
                  ? t('organisation:services.modal.titleDuplicate')
                  : t('organisation:services.modal.titleEdit')}
            </span>
            <button
              className="modal-close is-large"
              aria-label={t('organisation:shared.closeAriaLabel')}
              onClick={cancelServiceChanges}
            />
          </header>
          <Formik
            enableReinitialize={true}
            initialValues={{
              id: service.id || '',
              name: service.name || '',
              description: service.description || '',
              imageInCategory: {
                file: false,
                image:
                  getAssetLink({
                    path: 'chitown-default',
                    file: service.imageInCategory,
                  }) || '',
              },
              image: {
                file: false,
                image: getAssetLink({ path: 'chitown-default', file: service.image }) || '',
              },
              icon: service.icon || '',
              color: service.color || '',
              category: service.category || '',
              active: service.active ?? true,
              activeType: service.activeType ?? true,
              internalName: isDuplicate() ? '' : service.internalName || '',
              bookingType: service.bookingType
                ? typeof service.bookingType === 'number'
                  ? service.bookingType
                  : service.bookingType.id
                : '',
            }}
            onSubmit={saveService}
            validationSchema={validationSchema}
          >
            {({ handleSubmit, handleChange, values, errors, touched, setFieldTouched, dirty }) => (
              <form onSubmit={handleSubmit} className="modal__inner form">
                <div className="columns">
                  <div className="column">
                    <div className="modal__section modal__main-info">
                      <div className="modal__icon" style={{ backgroundColor: values.color }}>
                        <Icon icon={values.icon} />
                      </div>
                      <div className="modal__name">
                        <TextField
                          name={'name'}
                          placeholder={t('organisation:services.modal.name.placeholder')}
                          label={t('organisation:services.modal.name.label')}
                          value={values.name}
                          isError={touched.name && !!errors.name}
                          onChange={handleChange}
                          onBlur={() => setFieldTouched('name')}
                        />
                        <TextField
                          name={'internalName'}
                          label={t('organisation:services.modal.internalName.label')}
                          onChange={handleChange}
                          value={values.internalName}
                          placeholder={t('organisation:services.modal.internalName.placeholder')}
                          onBlur={() => setFieldTouched('internalName')}
                          isError={touched.internalName && !!errors.internalName}
                        />
                      </div>
                    </div>
                    <div className="modal__section modal__icons">
                      <span className="modal__section-label">
                        {t('organisation:services.modal.chooseIcon')}
                      </span>
                      <ul>
                        {SERVICE_ICONS.map((icon) => {
                          return (
                            <li key={icon}>
                              <RadioField
                                name={'icon'}
                                value={icon}
                                checked={values.icon === icon}
                                label={<Icon icon={icon} />}
                                onChange={handleChange}
                              />
                            </li>
                          )
                        })}
                      </ul>
                    </div>
                    <div className="modal__section modal__colors">
                      <span className="modal__section-label">
                        {t('organisation:services.modal.chooseColor')}
                      </span>
                      <ul>
                        {SERVICE_COLORS.map((color) => {
                          return (
                            <li key={color}>
                              <RadioField
                                name={'color'}
                                value={color}
                                checked={values.color === color}
                                label={
                                  <span
                                    className="color-label"
                                    style={{ backgroundColor: color }}
                                  />
                                }
                                onChange={handleChange}
                              />
                            </li>
                          )
                        })}
                      </ul>
                    </div>
                    <div className="modal__section modal__category">
                      <SelectField
                        name={'category'}
                        label={t('organisation:services.modal.category.label')}
                        options={SERVICE_CATEGORIES.map((service) => ({
                          value: service,
                          label: service,
                        }))}
                        isError={touched.category && !!errors.category}
                        value={values.category}
                        onChange={handleChange}
                      />
                      <SelectField
                        name={'bookingType'}
                        label={t('organisation:services.modal.bookingType.label')}
                        options={bookingTypes.map(({ id, text }) => ({
                          value: id,
                          label: text,
                        }))}
                        value={values.bookingType}
                        onChange={handleChange}
                      />
                      <div className="modal__togglesection">
                        <ToggleField
                          name={'active'}
                          label={t('organisation:services.modal.isActive')}
                          value={values.active}
                          onChange={handleChange}
                        />
                        <ToggleField
                          name={'activeType'}
                          label={t('organisation:shared.fields.availableOnline')}
                          value={values.activeType}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="column">
                    <div className="modal__section">
                      <ImageField
                        name={'imageInCategory'}
                        label={t('organisation:services.modal.imageInCategory.label')}
                        imageRatio={3 / 1}
                        onChange={handleChange}
                        value={values.imageInCategory}
                        placeholderImage="/images/placeholder.png"
                        additionalClass={'field-image--big'}
                      />
                    </div>
                    <div className="modal__section">
                      <ImageField
                        name={'image'}
                        label={t('organisation:services.modal.mainImage.label')}
                        placeholderImage="/images/placeholder.png"
                        imageRatio={16 / 9}
                        onChange={handleChange}
                        value={values.image}
                        additionalClass={'field-image--big'}
                      />
                    </div>
                    <div className="modal__section">
                      <br />
                      <TextAreaField
                        name={'description'}
                        placeholder={t('organisation:services.modal.description.placeholder')}
                        label={t('organisation:services.modal.description.label')}
                        value={values.description}
                        onChange={handleChange}
                        isError={touched.description && !!errors.description}
                        onBlur={() => setFieldTouched('description')}
                      />
                    </div>
                  </div>
                </div>
                <div className="modal__section modal__actions">
                  {!isNew() && !isDuplicate() && (
                    <span className="link" onClick={openConfirmModal}>
                      {t('organisation:services.modal.delete')}
                    </span>
                  )}
                  <button
                    type="button"
                    className="button button-gray is-block"
                    onClick={cancelServiceChanges}
                  >
                    {t('common:actions.cancel')}
                  </button>
                  <button
                    type="submit"
                    className={`button button-primary is-block ${formPending ? 'is-loading' : ''}`}
                    disabled={formPending || !dirty}
                  >
                    {isDuplicate()
                      ? t('organisation:services.item.duplicate')
                      : isNew()
                        ? t('organisation:shared.actions.create')
                        : t('common:actions.save')}
                  </button>
                  <p className="help is-danger">{formError}</p>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </div>
      <ConfirmPopup
        cancelText={t('common:actions.cancel')}
        confirmText={t('common:actions.confirm')}
        title={t('organisation:services.modal.deleteConfirm.title')}
        isOpen={modalConfirmOpened}
        cancelCallback={closeConfirmModal}
        confirmCallback={deleteService}
      />
    </div>
  )
}

export default OrganisationServicesModal
